import { createTheme, type Theme } from "@mui/material";
import { deepPurple, grey } from "@mui/material/colors";
import type {} from "@mui/x-data-grid-pro/themeAugmentation";
import type {} from "@mui/x-date-pickers/themeAugmentation";

import {
  REBRAND_ALERT_COLORS,
  REBRAND_BACKGROUND_PAGE,
  REBRAND_CHIP_COLORS,
  REBRAND_DANGER_COLOR,
  REBRAND_GRAY_4F,
  REBRAND_GRAY_E0,
  REBRAND_HEADER_DARK,
  REBRAND_LINK_COLOR,
  REBRAND_MUTED_COLOR,
  REBRAND_PRIMARY_COLOR,
  REBRAND_SECONDARY_COLOR,
  REBRAND_TEXT_STRONG,
  REBRAND_WARNING_COLOR,
} from "./rebrandConstants";
import { type TPaletteMode } from "./types";

const FONT_FAMILY_SANS = '"Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
const FONT_FAMILY_SERIF = '"Lora", Georgia, "Times New Roman", serif';

export function getRebrandTheme(paletteMode: TPaletteMode): Theme {
  const modeIsLight = paletteMode === "light";
  const textColor = modeIsLight ? REBRAND_TEXT_STRONG : "#FFFEFC";

  return createTheme({
    shape: {
      borderRadius: 8,
    },
    palette: {
      primary: {
        main: REBRAND_SECONDARY_COLOR,
      },
      error: {
        main: REBRAND_DANGER_COLOR,
        light: "#EF5350",
        contrastText: "#FFD9D2",
      },
      warning: { main: REBRAND_WARNING_COLOR },
      info: { main: REBRAND_PRIMARY_COLOR },
      secondary: {
        main: REBRAND_MUTED_COLOR,
        light: REBRAND_GRAY_E0,
        dark: REBRAND_GRAY_4F,
        contrastText: "#ffffff",
      },
      background: { default: REBRAND_BACKGROUND_PAGE },
      accent: {
        main: deepPurple[800],
        contrastText: "#ffffff",
      },
      pink: {
        main: "#FF20A6",
        light: "#F4D3EA",
      },
      blue600: {
        main: "#082A74",
        contrastText: "#E8F4FF",
      },
      green600: {
        main: "#169563",
        contrastText: "#E0FBEF",
      },
      destructiveButton: {
        main: REBRAND_DANGER_COLOR,
        contrastText: "#ffffff",
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 400,
        md: 600,
        lg: 900,
        xl: 1400,
      },
    },
    typography: {
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 400,
      fontWeightBold: 700,
      allVariants: {
        fontFamily: FONT_FAMILY_SANS,
        color: textColor,
        fontWeight: 400,
        lineHeight: "inherit",
      },
      h1: {
        fontSize: "1.75rem",
        fontFamily: FONT_FAMILY_SERIF,
      },
      h2: {
        fontSize: "1.5rem",
        fontFamily: FONT_FAMILY_SERIF,
      },
      h3: {
        fontSize: "1.125rem",
        lineHeight: "1.125rem",
        fontFamily: FONT_FAMILY_SERIF,
      },
      h4: {
        fontSize: "1rem",
      },
      body1: {
        fontSize: "0.875rem",
      },
      body2: {
        fontSize: "0.875rem",
        color: modeIsLight ? REBRAND_MUTED_COLOR : "#FFFEFC",
      },
      caption: {
        lineHeight: "1rem",
      },
    },
    components: {
      MuiLink: {
        defaultProps: {
          underline: "always",
        },
        styleOverrides: {
          root: {
            color: REBRAND_LINK_COLOR,
            textDecorationColor: REBRAND_LINK_COLOR,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardWarning: REBRAND_ALERT_COLORS.warning,
          standardInfo: REBRAND_ALERT_COLORS.info,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "unset",
          },
          contained: {
            boxShadow: "none",
            "&:hover": {
              color: "white",
              boxShadow: "none",
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "capitalize",
            "&:hover": {
              color: REBRAND_SECONDARY_COLOR,
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "8px",
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            color: REBRAND_DANGER_COLOR,
          },
          root: {
            paddingBottom: "4px",
          },
        },
      },
      MuiDateCalendar: {
        styleOverrides: {
          root: {
            width: "100%",
            border: `solid 1px ${grey[300]}`,
            borderRadius: "8px",
            maxHeight: "max-content",
            height: "auto",
            "& .MuiTypography-caption": {
              margin: "auto",
            },
            "& .MuiPickersDay-dayWithMargin": {
              margin: "auto",
            },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            "&:last-child": {
              paddingBottom: 16,
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: "none",
            "--DataGrid-overlayHeight": "320px",
          },
          columnHeaders: {
            borderBottom: "none",
            backgroundColor: REBRAND_HEADER_DARK,
            color: "white",
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          },
          cell: {
            "&:focus-within": {
              outline: "none",
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            border: `solid 1px ${grey[300]}`,
            borderRadius: "8px",
            "&::before": {
              display: "none",
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState: { color } }) => {
            if (color && color in REBRAND_CHIP_COLORS) {
              return REBRAND_CHIP_COLORS[color as keyof typeof REBRAND_CHIP_COLORS];
            }
            return {};
          },
        },
      },
    },
  });
}
